import { useEffect, useId, useRef } from 'react';
import { Button, Text } from '@coreai/fluentui-react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useFloatingPanelStyles } from './floatingPanelStyles';

/**
 * WorkflowCreatedPrompt — Stage 3 of the multi-step "Create new workflow" flow.
 *
 * Shown when a user creates a new workflow while already viewing another
 * one. Instead of silently navigating away (the legacy behaviour), we ask
 * whether to open the new workflow or stay on the current one. This
 * preserves the user's place in the workflow they were authoring.
 *
 * UX details:
 *  - Centered modal surface (via `useFloatingPanelStyles`) with a subtle
 *    backdrop dimming the canvas. The backdrop preserves spatial context
 *    while clearly signalling that the prompt owns the user's attention
 *    until they pick an option.
 *  - role="alertdialog" + aria-modal="true": this surface owns a decision,
 *    and AT users should be alerted to it on mount.
 *  - `useFocusTrap` keeps Tab inside the panel and restores focus to the
 *    triggering element on unmount.
 *  - Primary button ("Open new workflow") receives focus on mount so
 *    keyboard users can confirm with Enter immediately.
 *  - Esc invokes `onStay` (stay = the safe default that preserves the
 *    user's current context); listener runs in the capture phase with
 *    `stopImmediatePropagation` so the modal genuinely owns the gesture.
 */

export interface WorkflowCreatedPromptProps {
  /** The freshly created workflow's display name (already extracted). */
  newWorkflowName: string;
  /** Invoked when the user picks "Open new workflow". */
  onOpenNew: () => void;
  /** Invoked when the user picks "Stay here" (or presses Esc). */
  onStay: () => void;
}

export const WorkflowCreatedPrompt = ({
  newWorkflowName,
  onOpenNew,
  onStay,
}: WorkflowCreatedPromptProps) => {
  const styles = useFloatingPanelStyles();
  const titleId = useId();
  const descriptionId = useId();
  // Focus-trap refs. `useFocusTrap` manages everything the prompt's modal
  // contract requires: captures the trigger on mount, moves focus to the
  // primary button, traps Tab inside the panel, and restores focus on
  // unmount. Replaces the previous bespoke `useRestoreFocusOnUnmount` +
  // `autoFocus` + manual focus-on-mount effect.
  const panelRef = useRef<HTMLDivElement>(null);
  const primaryButtonRef = useRef<HTMLButtonElement | null>(null);

  useFocusTrap({
    active: true,
    containerRef: panelRef,
    initialFocusRef: primaryButtonRef,
  });

  // Esc = Stay (the safe default — preserves user's current context).
  // Listener runs in the capture phase so we own the Esc gesture before
  // any element-level or document-level handlers see it; `stopImmediate-
  // Propagation` prevents the event from reaching the rest of the chain.
  // Matches alertdialog semantics: while this surface is mounted, no
  // other handler on the page should also react to Esc (e.g. closing an
  // unrelated popover the user can't even see right now).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onStay();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [onStay]);

  return (
    <div className={styles.viewport} data-testid="workflow-created-prompt">
      <div
        ref={panelRef}
        className={styles.panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <Text id={titleId} as="h2" className={styles.title}>
          Workflow created
        </Text>
        <Text id={descriptionId} as="p" className={styles.body}>
          Your workflow &ldquo;{newWorkflowName}&rdquo; has been created. Would
          you like to stay on the current workflow or open the new one?
        </Text>
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={onStay}>
            Stay here
          </Button>
          <Button
            ref={(el: HTMLButtonElement | null) => {
              primaryButtonRef.current = el;
            }}
            appearance="primary"
            onClick={onOpenNew}
          >
            Open new workflow
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowCreatedPrompt;
